import React from 'react';
import { View, Svg, Rect, Text, Path, Line, Circle } from '@react-pdf/renderer';
import type { Goal } from '../types';

interface ShotPositionAnalyticsPDFProps {
  goals: Goal[];
  width?: number;
  height?: number;
}

const ShotPositionAnalyticsPDF: React.FC<ShotPositionAnalyticsPDFProps> = ({ 
  goals,
  width = 200,
  height = 150
}) => {
  // Calculate zones (3x3 grid)
  const getZone = (x: number | null, y: number | null): number => {
    if (x === null || y === null) return -1;
    const col = Math.floor(x / 33.33);
    const row = Math.floor(y / 33.33);
    return row * 3 + col;
  };

  // Count goals in each zone
  const zoneCount = new Array(9).fill(0);
  goals.forEach(goal => {
    const zone = getZone(goal.shotXPosition, goal.shotYPosition);
    if (zone >= 0 && zone < 9) {
      zoneCount[zone]++;
    }
  });

  // Calculate percentages
  const goalsWithShotPosition = goals.filter(goal => goal.shotXPosition !== null && goal.shotYPosition !== null);
  const totalGoals = goalsWithShotPosition.length;
  const zonePercentages = zoneCount.map(count => 
    totalGoals > 0 ? (count / totalGoals) * 100 : 0
  );

  return (
    <View style={{ width, height }}>
      <Svg viewBox="0 0 100 100" width={width} height={height}>
        {/* Blue line at the top */}
        <Line x1={0} y1={0} x2={100} y2={0} stroke="#00A3E0" strokeWidth={2} />

        {/* Rink outline */}
        <Path
          d="M 0 0 L 0 80 Q 0 100 20 100 L 80 100 Q 100 100 100 80 L 100 0"
          stroke="#000000"
          strokeWidth={1}
          fill="none"
        />

        {/* Center ice semi-circle */}
        <Path
          d="M 40 100 A 10 10 0 0 1 60 100"
          stroke="#00A3E0"
          strokeWidth={1}
          fill="#00A3E0"
          fillOpacity={0.1}
        />

        {/* Grid lines */}
        <Line x1={33.33} y1={0} x2={33.33} y2={100} stroke="#E5E7EB" strokeWidth={0.5} />
        <Line x1={66.66} y1={0} x2={66.66} y2={100} stroke="#E5E7EB" strokeWidth={0.5} />
        <Line x1={0} y1={33.33} x2={100} y2={33.33} stroke="#E5E7EB" strokeWidth={0.5} />
        <Line x1={0} y1={66.66} x2={100} y2={66.66} stroke="#E5E7EB" strokeWidth={0.5} />

        {/* Zone percentages */}
        {zonePercentages.map((percentage, index) => {
          if (percentage === 0) return null;

          const col = index % 3;
          const row = Math.floor(index / 3);
          const x = col * 33.33;
          const y = row * 33.33;
          const centerX = x + 16.665;
          const centerY = y + 16.665;

          return (
            <React.Fragment key={index}>
              <Rect
                x={x}
                y={y}
                width={33.33}
                height={33.33}
                fill={`rgba(239, 68, 68, ${percentage / 100})`}
                fillOpacity={0.3}
              />
              <Circle
                cx={centerX}
                cy={centerY}
                r={8}
                fill="#ef4444"
              />
              <Text
                x={centerX}
                y={centerY}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fontSize: 6,
                  fontWeight: 'bold',
                  fill: '#ffffff'
                }}
              >
                {`${Math.round(percentage)}%`}
                </Text>
            </React.Fragment>
          );
        })}
      </Svg>
    </View>
  );
};

export default ShotPositionAnalyticsPDF;